import loadScript from 'load-script';
import isProduction from './standalone/isProduction';
import { siteConfig } from './util/siteConfig';
var GOOGLE_SDK_URL = 'https://apis.google.com/js/api.js';
var MIME = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gdriveDocument: 'application/vnd.google-apps.document'
};
var onApiLoad = function onApiLoad() {
  return new Promise(function (resolve, reject) {
    window.gapi.load('auth,picker', {
      callback: resolve,
      onerror: function onerror(err) {
        return reject(new Error('error loading gapi libraries', err));
      }
    });
  });
};
var init = function init() {
  return new Promise(function (resolve, reject) {
    loadScript(GOOGLE_SDK_URL, function (err, script) {
      if (err) {
        return reject(new Error('error loading gapi', err));
      }
      return resolve(script);
    });
  });
};
var authorize = function authorize(immediate, scope, site) {
  return new Promise(function (resolve, reject) {
    var _siteConfig = siteConfig(site, !isProduction()),
      gdrive = _siteConfig.gdrive;
    if (!window.gapi || !window.gapi.auth || !window.google.picker) {
      return reject(new Error('not loaded yet'));
    }
    var token = window.gapi.auth.getToken();
    var oauthToken = token && token.access_token;
    if (oauthToken) {
      return resolve(oauthToken);
    }
    return window.gapi.auth.authorize({
      client_id: gdrive.oauthId,
      scope: scope,
      immediate: immediate
    }, function (result) {
      resolve(result.access_token);
    });
  });
};
var uploadFile = function uploadFile(blob, metadata, contentType) {
  var boundary = '-------314159265358979323846';
  var delimiter = "\r\n--".concat(boundary, "\r\n");
  var closeDelimiter = "\r\n--".concat(boundary, "--");
  var reader = new FileReader();
  reader.readAsBinaryString(blob);
  return new Promise(function (resolve) {
    reader.onload = function () {
      var base64Data = btoa(reader.result);
      /* eslint-disable prefer-template */
      var multipartRequestBody = delimiter + 'Content-Type: application/json\r\n\r\n' + JSON.stringify(metadata) + delimiter + 'Content-Type: ' + contentType + '\r\n' + 'Content-Transfer-Encoding: base64\r\n\r\n' + base64Data + closeDelimiter;
      /* eslint-enable prefer-template */

      var request = window.gapi.client.request({
        path: '/upload/drive/v3/files',
        method: 'POST',
        params: {
          uploadType: 'multipart'
        },
        headers: {
          'Content-Type': "multipart/related; boundary=\"".concat(boundary, "\"")
        },
        body: multipartRequestBody
      });
      request.execute(function (response) {
        return resolve(response);
      });
    };
  });
};
var isGoogleDoc = function isGoogleDoc(metaData) {
  return metaData.mimeType === MIME.gdriveDocument;
};
var getFileName = function getFileName(metaData) {
  return isGoogleDoc(metaData) ? "".concat(metaData.name, ".docx") : metaData.name;
};
var getFileBlob = function getFileBlob(fileData, metaData) {
  return isGoogleDoc(metaData) ? fileData : new Blob([fileData], {
    type: metaData.mimeType
  });
};
function getDownloadUrl(site, metaData, id) {
  var url = "".concat(siteConfig(site, !isProduction()).gdrive.endpoint).concat(id);
  if (isGoogleDoc(metaData)) {
    url += "/export?mimeType=".concat(encodeURIComponent(MIME.docx));
  } else {
    url += '?alt=media';
  }
  return url;
}
var openPicker = function openPicker(accessToken, title, views, features, mimeTypes, site) {
  var _siteConfig2 = siteConfig(site, !isProduction()),
    gdrive = _siteConfig2.gdrive;
  var builder = new window.google.picker.PickerBuilder().setAppId(gdrive.appId).setDeveloperKey(gdrive.apiKey).setOAuthToken(accessToken).setTitle(title).setSelectableMimeTypes(mimeTypes.join(','));
  views.forEach(function (view) {
    return builder.addView(view);
  });
  features.forEach(function (feature) {
    return builder.enableFeature(feature);
  });
  return new Promise(function (resolve) {
    builder.setCallback(function (data) {
      if (data.action === 'picked') {
        resolve(data);
      }
    });
    var picker = builder.build();
    picker.setVisible(true);
  });
};
var Gapi = {
  init: init,
  onApiLoad: onApiLoad,
  uploadDocx: function uploadDocx(name, blob) {
    var folderId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    var site = arguments.length > 3 ? arguments[3] : undefined;
    return authorize(false, siteConfig(site, !isProduction()).gdrive.scope, site).then(function () {
      // upload into a Google Document
      var metadata = {
        name: name,
        mimeType: MIME.gdriveDocument
      };
      if (folderId) {
        metadata.parents = [folderId];
      }
      // the actual upload must use the correct mime type (docx)
      return uploadFile(blob, metadata, MIME.docx);
    });
  },
  downloadDocx: function downloadDocx(id, site) {
    return authorize(false, siteConfig(site, !isProduction()).gdrive.scope, site).then(function (accessToken) {
      var requestOptions = {
        method: 'GET',
        headers: {
          Authorization: "Bearer ".concat(accessToken)
        }
      };

      // get meta infos about the document at $id
      return fetch("".concat(siteConfig(site, !isProduction()).gdrive.endpoint).concat(id, "?fields=*"), requestOptions).then(function (response) {
        return response.json();
      }).then(function (metadata) {
        var url = getDownloadUrl(site, metadata, id);
        return fetch(url, requestOptions).then(function (documentResponse) {
          if (!documentResponse.ok) {
            throw new Error('failed to download document');
          }
          return documentResponse.blob();
        }).then(function (fileData) {
          var blob = getFileBlob(fileData, metadata);
          var fname = getFileName(metadata);
          return {
            file: Object.assign(blob, {
              name: fname,
              filename: fname
            })
          };
        });
      });
    });
  },
  openDocumentPicker: function openDocumentPicker(site, upload) {
    return authorize(false, siteConfig(site, !isProduction()).gdrive.scope, site).then(function (accessToken) {
      var view = new window.google.picker.View(window.google.picker.ViewId.DOCS);
      view.setMimeTypes(upload.mimeTypes.join(','));
      return openPicker(accessToken, 'Choose a document', [view], [window.google.picker.Feature.NAV_HIDDEN], upload.mimeTypes, site);
    });
  }
};
export default Gapi;